import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { FaClock, FaEye, FaHeart, FaShare } from 'react-icons/fa';
import SearchBar from '../common/SearchBar';
import RecentPosts from '../common/RecentPosts';
import PopularPosts from '../common/PopularPosts';
import TagsList from '../common/TagsList';
import CategoryList from '../common/CategoryList';
import StatusBadge from '../../common/StatusBadge';

const CaseStudyList = ({ caseStudies = [] }) => {
  if (!caseStudies.length) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 dark:text-gray-400">No case studies found.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
      {/* Main Content Column */}
      <div className="col-span-3 space-y-8">
        <h1 className="text-2xl font-bold text-gray-700 dark:text-gray-200">Case Studies</h1>
        {caseStudies.map((caseStudy) => (
          <div key={caseStudy._id} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
            {caseStudy.featureImage && (
              <img
                src={caseStudy.featureImage}
                alt={caseStudy.title}
                className="w-full h-48 object-cover rounded-t-lg mb-4"
              />
            )}
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {format(new Date(caseStudy.publishedAt || caseStudy.createdAt), 'MMM d, yyyy')}
                  </span>
                  <StatusBadge status={caseStudy.status} />
                </div>
                {caseStudy.featured && (
                  <span className="px-2 py-1 text-xs font-semibold text-white bg-blue-600 rounded-full">
                    Featured
                  </span>
                )}
              </div>
              <div className="mt-2">
                <Link 
                  to={`/case-studies/${caseStudy.urlSlug}`} 
                  className="text-2xl font-bold text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {caseStudy.title}
                </Link>
                <p className="mt-2 text-gray-600 dark:text-gray-400">{caseStudy.shortDescription || caseStudy.description}</p>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center space-x-4">
                  <Link 
                    to={`/case-studies/${caseStudy.urlSlug}`} 
                    className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    Read more
                  </Link>
                  <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <FaClock className="mr-1" />
                    {caseStudy.projectDetails?.duration || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <FaEye className="mr-1" />
                    {caseStudy.metadata?.views || 0}
                  </span>
                  <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <FaHeart className="mr-1" />
                    {caseStudy.metadata?.likes || 0}
                  </span>
                  <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                    <FaShare className="mr-1" />
                    {caseStudy.metadata?.shares || 0}
                  </span>
                </div>
              </div>
              <div className="flex items-center mt-4">
                <img
                  src={caseStudy.author?.avatar || 'https://via.placeholder.com/50'}
                  alt={caseStudy.authorName}
                  className="w-10 h-10 rounded-full hidden sm:block"
                />
                <span className="ml-3 font-medium text-gray-700 dark:text-gray-200">
                  {caseStudy.authorName}
                </span>
              </div>
              {(caseStudy.categoryNames?.length > 0 || caseStudy.tagNames?.length > 0) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {caseStudy.categoryNames?.map((category, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                    >
                      {category}
                    </span>
                  ))}
                  {caseStudy.tagNames?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Sidebar Column */}
      <div className="col-span-1 space-y-6">
        <SearchBar />
        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-lg font-bold text-gray-700 dark:text-gray-200">About Our Case Studies</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Explore our portfolio of successful projects and learn how we've helped businesses achieve their goals.
          </p>
        </div>
        <CategoryList />
        <RecentPosts />
        <PopularPosts />
        <TagsList />
      </div>
    </div>
  );
};

export default CaseStudyList; 